import { useEffect, useState } from 'react'
import type { ApiClient, JournalSummary, JournalView } from '../api/client'
import { describeError, type Problem } from '../i18n/errors'
import { ProblemLine } from '../ProblemLine'
import { summaryKeyLabel, sourceLabel } from '../i18n/derived'
import { plural, t } from '../i18n/dict'
import { duration, money, pct, when } from '../i18n/format'
import { formats } from '../i18n/locale'

/**
 * The trade journal. It leads with the cut by source, because that is the one the rest of the
 * system is an argument about: if trades typed into the native ticket do not perform differently
 * from those placed through the Gatekeeper, the ergonomics case is wrong and worth finding out.
 * No CSV here — downloads inside a cTrader webview are unreliable; the TUI keeps that.
 */
export function Journal({ api, account }: { api: ApiClient; account: number }) {
  const [selected, setDays] = useState(30)
  const [view, setView] = useState<JournalView | null>(null)
  const [error, setError] = useState<Problem | null>(null)

  useEffect(() => {
    setView(null)
    api.journal(account, selected).then(setView).catch((e: unknown) => setError(describeError(e)))
  }, [api, account, selected])

  if (error && !view) return <ProblemLine problem={error} />
  if (!view) return <p className="muted">{t('journal.reading')}</p>

  const days = (n: number) => plural('common.days', n, { count: formats().integer.format(n) })

  return (
    <section className="console">
      <p>
        <strong>{t('common.accountN', { id: view.account })}</strong>{' '}
        <span className="muted">{t('common.window', { days: days(view.days), until: when(view.until) })}</span>
        <select
          value={selected}
          onChange={(e) => setDays(Number(e.target.value))}
          aria-label={t('common.daysAria')}
          className="days"
        >
          {[7, 30, 90].map((d) => (
            <option key={d} value={d}>
              {days(d)}
            </option>
          ))}
        </select>
      </p>

      {view.isEmpty ? (
        <div className="panel yellow">
          <strong>{t('journal.empty.title')}</strong>
          <p>
            <strong>{t('journal.empty.head')}</strong>
          </p>
          <p>{t('journal.empty.body')}</p>
        </div>
      ) : (
        <>
          <SummaryTable title={t('journal.bySource')} rows={view.bySource} />
          <SummaryTable title={t('journal.byProtection')} rows={view.byProtection} />
          <SummaryTable title={t('journal.byWeekday')} rows={view.byWeekday} />
          <div className="table-wrap">
            <table>
              <caption>{t('journal.trades')}</caption>
              <thead>
                <tr>
                  <th>{t('journal.th.closed')}</th>
                  <th>{t('journal.th.source')}</th>
                  <th>{t('journal.th.side')}</th>
                  <th>{t('journal.th.entry')}</th>
                  <th>{t('journal.th.exit')}</th>
                  <th>{t('journal.th.held')}</th>
                  <th>{t('journal.th.stop')}</th>
                  <th>{t('journal.th.net')}</th>
                </tr>
              </thead>
              <tbody>
                {view.trades.map((row) => (
                  <tr key={`${row.positionId}-${row.leg}`}>
                    <td className="muted">{when(row.closedAt)}</td>
                    <td>
                      {row.source === 'gatekeeper' ? (
                        <span className="good">{sourceLabel(row.source)}</span>
                      ) : (
                        sourceLabel(row.source)
                      )}
                    </td>
                    <td>{row.side ?? t('unit.none')}</td>
                    <td>{row.entryPrice ?? t('unit.none')}</td>
                    <td>{row.exitPrice ?? t('unit.none')}</td>
                    <td>{duration(row.heldMinutes)}</td>
                    <td>
                      {row.openedBeforeWeWatched ? (
                        <span className="muted">{t('journal.stop.unseen')}</span>
                      ) : row.wasNaked ? (
                        <span className="bad">{t('journal.stop.naked')}</span>
                      ) : row.guardianIntervened ? (
                        <span className="warn">{t('journal.stop.guardian')}</span>
                      ) : (
                        <span className="good">{t('journal.stop.atEntry')}</span>
                      )}
                    </td>
                    <td className={row.net >= 0 ? 'good' : 'bad'}>{money(row.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}

function SummaryTable({ title, rows }: { title: string; rows: JournalSummary[] }) {
  if (rows.length === 0) return null
  return (
    <div className="table-wrap">
      <table>
        <caption>{title}</caption>
        <thead>
          <tr>
            <th></th>
            <th>{t('journal.sum.trades')}</th>
            <th>{t('journal.sum.winRate')}</th>
            <th>{t('journal.sum.net')}</th>
            <th>{t('journal.sum.average')}</th>
            <th>{t('journal.sum.medianHold')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>{summaryKeyLabel(r.key)}</td>
              <td>{formats().integer.format(r.count)}</td>
              <td>{pct(Math.round(r.winRate * 100))}</td>
              <td className={r.net >= 0 ? 'good' : 'bad'}>{money(r.net)}</td>
              <td>{money(r.average)}</td>
              <td className="muted">{duration(r.medianHoldMinutes)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
